/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { Button } from "@mui/material";
import "./multiLocQuotePanel.css";

// Icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import SearchIcon from "@mui/icons-material/Search";
import { sortDestinations } from "../../multiLocationQuotePanel/components/multiLocationQuoteUtils";
import { icons } from "../../../svg";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";

import MobileProductFilterContainer from "./components/mobileProductContainer";
import LoadingIcon from "../../../components/Loading";
import { IOccupant } from "../../../types/ILocation";
import NitroService from "../../../utils/nitro.service";
import {
  MultiLocQuotePanelProps,
  DestinationQuote,
  DestinationQuoteWithSummary,
  Quote,
} from "../../../types/IMultiLocationQuoteMobile";
import LocationQuoteCard from "./components/LocationQuoteCard";
import {
  generateSplitAwareMonths,
  findSelectedMonthOption,
} from "../multiLocQuoteDetailsPanel/multiLocQuoteDetailsUtils";
import MultiLocQuoteDetailsPanel from "../multiLocQuoteDetailsPanel/multiLocQuoteDetailsPanel";
import { IFavoriteLocationExtension } from "../../../types/IFavoriteOrigins";

const MultiLocQuotePanel: React.FC<MultiLocQuotePanelProps> = ({
  companyName,
  quotes,
  setQuotes,
  setSurroundingLocs,
  surroundingLocs,
  onBack,
  selectedProduct,
  setSelectedProduct,
  selectedMonth,
  setSelectedMonth,
  onOpenOrigins,
  loading,
  triggerRefetch,
  hookDestinationQuotes,
  hookSurroundingLocations,
  selectedDestination,
  setSelectedDestination,
}) => {
  const [expandWithout, setExpandWithout] = useState(false);
  const [expandSurrounding, setExpandSurrounding] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const prevExpandWithoutRef = useRef<boolean | null>(null);
  const prevExpandSurroundingRef = useRef<boolean | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const [selectedItemState, setSelectedItemState] = useState<any>(null);
  const [locationValidationTrigger, setLocationValidationTrigger] = useState(0);

  const [priorityProducts, currentFilters, favoriteOrigins] = useNitroStore(
    (state) => [
      state.priorityProducts,
      state.currentFilters,
      state.favoriteOrigins,
    ],
  );

  const [
    setTroubleshootDestinationOpen,
    setDestinationToTroubleshoot,
    setExpandedDestination,
    hiddenOrigins,
    triggerRefetchQuotes,
    refetchQuotes,
  ] = useNitroCAStore((state) => [
    state.setDisplayAddProductToDestinationPanel,
    state.setDestinationToTroubleshoot,
    state.setExpandedDestination,
    state.hiddenOrigins,
    state.triggerRefetchQuotes,
    state.refetchQuotes,
  ]);
  const queryType =
    currentFilters.city || currentFilters.location ? "location" : "party";
  const effectiveSelectedProduct = selectedProduct || "AMMONIA";
  const setSelectedItem = setSelectedItemState;
  const selectedItem = selectedItemState;
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (selectedItem?.destination?.id) {
      setSelectedDestination(selectedItem.destination.id);
      hasMountedRef.current = true;
    } else if (selectedItem === null && hasMountedRef.current) {
      setSelectedDestination(null);
    }
  }, [selectedItem, setSelectedDestination]);

  useEffect(() => {
    if (selectedDestination && quotes.length > 0 && !selectedItem) {
      const itemToRestore = quotes.find(
        (item) => item.destination.id === selectedDestination,
      );
      if (itemToRestore) {
        const summary = determineDisplayValues(itemToRestore.quotes);
        setSelectedItem({ ...itemToRestore, summary });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const isInitialMount = useRef(true);
  const prevProductRef = useRef(selectedProduct);
  const prevMonthRef = useRef(selectedMonth);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      prevProductRef.current !== selectedProduct ||
      prevMonthRef.current !== selectedMonth
    ) {
      triggerRefetch();
      prevProductRef.current = selectedProduct;
      prevMonthRef.current = selectedMonth;
    }
  }, [selectedProduct, selectedMonth, triggerRefetch]);

  useEffect(() => {
    // hook preserves previous data while loading, so this only reflects genuine fetch results (including empty)
    if (hookDestinationQuotes !== undefined) {
      setQuotes(hookDestinationQuotes);
      setSurroundingLocs(hookSurroundingLocations || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hookDestinationQuotes, hookSurroundingLocations]);

  useEffect(() => {
    const destinationToUpdate =
      selectedItem?.destination?.id || selectedDestination;

    if (destinationToUpdate) {
      if (quotes.length === 0 && surroundingLocs.length === 0) {
        setSelectedItem(null);
      } else {
        const findById = (items: any[]) =>
          items.find((item) => item.destination.id === destinationToUpdate);

        let updatedItem = findById(quotes);

        if (!updatedItem && surroundingLocs.length > 0) {
          updatedItem = findById(surroundingLocs as any[]);
        }

        if (updatedItem) {
          const summary = determineDisplayValues(updatedItem.quotes);
          setSelectedItem({ ...updatedItem, summary });
        } else {
          console.log(
            "multiLocQuotePanel: Selected destination not found, closing",
          );
          setSelectedItem(null);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quotes, selectedDestination, surroundingLocs]);

  const splitMonthKeys = useMemo(() => {
    const keys = new Set<string>();
    const productEntry = favoriteOrigins.find(
      (o) =>
        o.product?.toUpperCase() === effectiveSelectedProduct.toUpperCase(),
    );
    (productEntry?.favoriteLocations || []).forEach((fav) => {
      fav.months.forEach((m) => {
        if (m.split) {
          const mk = (m.month as string).slice(0, 7);
          keys.add(mk);
        }
      });
    });
    return Array.from(keys);
  }, [favoriteOrigins, effectiveSelectedProduct]);

  const months = useMemo(
    () => generateSplitAwareMonths(splitMonthKeys),
    [splitMonthKeys],
  );

  const selectedMonthOption = useMemo(
    () => findSelectedMonthOption(months, selectedMonth),
    [months, selectedMonth],
  );

  const formatProductName = (name: string) => {
    return name.charAt(0).toUpperCase() + name.slice(1).toUpperCase();
  };

  const addProductToOccupant = async (
    occupant: IOccupant,
    destinationId: string,
    destinationName: string,
  ) => {
    const updatedOccupant = {
      ...occupant,
      products: [
        ...occupant.products,
        { product: effectiveSelectedProduct, quantity: 0 },
      ],
    };
    await NitroService.updateOccupants({
      id: destinationId,
      occupants: [updatedOccupant],
    });

    triggerRefetchQuotes();
    setExpandedDestination(
      {
        id: destinationId,
        name: destinationName,
      },
      {
        expandSurroundingLocations: false,
      },
    );
  };

  const renderProductButton = () => (
    <Button
      id="mobile-product-select-button"
      className="product-button-multi-loc"
      onClick={() => setIsFilterOpen(!isFilterOpen)}
      sx={{
        marginLeft: "10px",
        minWidth: "120px",
        maxWidth: "120px",
      }}
    >
      <span className="product-text" id="mobile-product-select-button-text">
        {formatProductName(effectiveSelectedProduct)}
      </span>
      <img
        id="mobile-product-select-button-icon"
        data-tooltip-id="downward-expand-icon"
        src={icons.DownwardExpandIcon}
        alt="Downward Expand Icon"
        className="expand-icon"
        style={{ filter: "brightness(0) invert(1)" }}
      />
    </Button>
  );

  const renderMonthButton = () => {
    const selectedMonthDisplay = selectedMonthOption?.display || "Select Month";
    return (
      <Button
        id="mobile-month-select-button"
        className="product-button-multi-loc"
        onClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}
      >
        <span className="product-text" id="mobile-month-select-button-text">
          {selectedMonthDisplay}
        </span>
        <img
          id="mobile-month-select-button-icon"
          src={icons.DownwardExpandIcon}
          alt="Downward Expand Icon"
          className="expand-icon"
          style={{ filter: "brightness(0) invert(1)" }}
        />
      </Button>
    );
  };

  // Only display the best offer for Koch TRUCK or RAIL, No competitor, and no Koch tratic rates(isExternal is true)
  const determineDisplayValues = useCallback(
    (quoteList: Quote[]) => {
      const getEffectiveNetback = (quote: Quote): number | null => {
        // When originManager is on, prefer postedPrice (matches "px" dlvd default in details panel)
        const nb = (quote as any).postedPrice ?? quote.maxPrice;
        return typeof nb === "number" && Number.isFinite(nb) ? nb : null;
      };

      const kochQuotesWithPricing = quoteList.filter((quote) => {
        if ((quote as any).competitor) return false;
        if (
          quote.freight?.totalFreight == null ||
          quote.freight.isExternal === true
        )
          return false;
        if (getEffectiveNetback(quote) === null) return false;

        return true;
      });

      if (kochQuotesWithPricing.length === 0) {
        return {
          deliveredPrice: "--",
          freight: "--",
          netback: 0,
          alternative: 0,
          isFreightValid: true,
          originName: "",
          mode: "",
          isExternal: false,
        };
      }

      const cheapestKochQuote = kochQuotesWithPricing.sort((a, b) => {
        const totalA =
          (getEffectiveNetback(a) ?? 0) + (a.freight?.totalFreight || 0);
        const totalB =
          (getEffectiveNetback(b) ?? 0) + (b.freight?.totalFreight || 0);
        return totalA - totalB;
      })[0];

      const { freight, minPrice, origin } = cheapestKochQuote;
      const effectiveNetback = getEffectiveNetback(cheapestKochQuote) ?? 0;
      const totalFreight = freight?.totalFreight ?? 0;
      // use DLVD offer to determin cheapest quote
      const initialOffer =
        effectiveNetback && totalFreight
          ? effectiveNetback + totalFreight
          : null;

      return {
        deliveredPrice: initialOffer ? initialOffer.toFixed(0) : "--",
        freight: totalFreight ? totalFreight.toFixed(2) : "--",
        netback: effectiveNetback ? effectiveNetback.toFixed(0) : "0",
        alternative: minPrice?.toFixed(0) || "0",
        isFreightValid:
          freight && "validation" in freight
            ? (freight as any).validation.isValid
            : true,
        originName: origin.name,
        mode: cheapestKochQuote.mode,
        isExternal: !!(freight as any)?.isExternal,
      };
    },
    [favoriteOrigins, effectiveSelectedProduct, refetchQuotes],
  );

  const { kochOrigins, competitorOrigins } = useMemo(() => {
    const originsForProduct =
      favoriteOrigins.find(
        (o) =>
          o.product.toUpperCase() === effectiveSelectedProduct.toUpperCase(),
      )?.favoriteLocations || [];

    const koch: IFavoriteLocationExtension[] = [];
    const competitor: IFavoriteLocationExtension[] = [];

    originsForProduct.forEach((origin) => {
      if (origin.competitor) {
        competitor.push(origin);
      } else {
        koch.push(origin);
      }
    });

    return {
      kochOrigins: koch,
      competitorOrigins: competitor,
    };
  }, [favoriteOrigins, effectiveSelectedProduct]);

  const { available, withoutProduct } = useMemo(() => {
    const filteredByModes = quotes.map((entry) => {
      const filteredQuotes = entry.quotes.filter((quote) => {
        const mode = quote.mode.toLowerCase();
        const originKey = `${quote.competitor ? "competitor" : "koch"}-${
          quote.origin?.name
        }`;

        if (mode === "truck" || mode === "rail") {
          return !hiddenOrigins[mode].has(originKey);
        }

        return true;
      });

      return {
        destination: entry.destination,
        quotes: filteredQuotes,
      };
    });

    const avail: DestinationQuoteWithSummary[] = [];
    const without: DestinationQuote[] = [];

    filteredByModes.forEach((dest) => {
      const destinationProducts = (
        (dest.destination as any).availableProducts || []
      ).map((p: string) => p.toUpperCase());
      const destinationHasProduct =
        destinationProducts.includes(effectiveSelectedProduct.toUpperCase()) ||
        (dest.destination as any).isCity;

      if (destinationHasProduct) {
        const summary = determineDisplayValues(dest.quotes);
        avail.push({ ...dest, summary });
      } else if (!(dest.destination as any).isCity) {
        // destinations without product with city pins excluded
        without.push(dest);
      }
    });

    return {
      available: sortDestinations(
        avail as any,
        queryType,
      ) as any as DestinationQuoteWithSummary[],
      withoutProduct: without,
    };
  }, [
    determineDisplayValues,
    queryType,
    quotes,
    effectiveSelectedProduct,
    hiddenOrigins,
  ]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) {
      return {
        availableFiltered: available,
        withoutFiltered: withoutProduct,
        surroundingFiltered: surroundingLocs || [],
      };
    }

    const matchByName = (item: any) => {
      const name = (item.destination?.name || "").toString().toLowerCase();
      return name.includes(q);
    };

    return {
      availableFiltered: available.filter((it) => matchByName(it)),
      withoutFiltered: withoutProduct.filter((it) => matchByName(it)),
      surroundingFiltered: (surroundingLocs || []).filter((it) =>
        matchByName(it),
      ),
    };
  }, [searchText, available, withoutProduct, surroundingLocs]);

  useEffect(() => {
    if (!isSearchOpen) return;

    const trimmedSearch = searchText.trim();

    if (!trimmedSearch) {
      if (prevExpandWithoutRef.current !== null)
        setExpandWithout(prevExpandWithoutRef.current);
      if (prevExpandSurroundingRef.current !== null)
        setExpandSurrounding(prevExpandSurroundingRef.current);
      return;
    }

    const hasWithout = (filtered.withoutFiltered || []).length > 0;
    const hasSurrounding = (filtered.surroundingFiltered || []).length > 0;

    setExpandWithout(hasWithout);
    setExpandSurrounding(hasSurrounding);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, isSearchOpen, searchText]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      try {
        searchInputRef.current.focus();
      } catch (e) {
        console.error("Failed to focus search input", e);
      }
    }
  }, [isSearchOpen]);

  if (selectedItem) {
    return (
      <MultiLocQuoteDetailsPanel
        key={`${selectedItem.destination.name}-${selectedItem.summary.deliveredPrice}-${selectedItem.quotes.length}`}
        item={selectedItem}
        onBack={() => setSelectedItem(null)}
        onSearchClick={() =>
          onBack(companyName || selectedItem.destination.name)
        }
        companyName={companyName}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        onOpenOrigins={onOpenOrigins}
        loading={loading}
        locationValidationTrigger={locationValidationTrigger}
      />
    );
  }

  return (
    <div id="quote-panel-id" className="quote-panel-container">
      {isFilterOpen && (
        <div
          role="button"
          tabIndex={0}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "flex-end",
          }}
          onClick={() => setIsFilterOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape" || e.key === "Enter") setIsFilterOpen(false);
          }}
        >
          <MobileProductFilterContainer
            products={priorityProducts}
            selectedProduct={selectedProduct}
            onProductSelect={(product) => {
              setSelectedProduct(product);
              setIsFilterOpen(false);
            }}
            onClose={() => setIsFilterOpen(false)}
          />
        </div>
      )}

      {isMonthPickerOpen && (
        <div
          role="button"
          tabIndex={0}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "flex-end",
          }}
          onClick={() => setIsMonthPickerOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape" || e.key === "Enter")
              setIsMonthPickerOpen(false);
          }}
        >
          <div
            style={{
              background: "white",
              width: "100%",
              borderTopLeftRadius: "16px",
              borderTopRightRadius: "16px",
              padding: "24px",
              maxHeight: "60vh",
              overflowY: "auto",
            }}
          >
            <div
              style={{
                fontSize: "18px",
                fontWeight: 600,
                marginBottom: "16px",
              }}
            >
              Select Month
            </div>
            <div
              className="month-scroll-container"
              style={{ display: "flex", flexDirection: "column", gap: "8px" }}
            >
              {months.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  className={`month-chip ${
                    m.value === selectedMonthOption?.value ? "active" : ""
                  }`}
                  onClick={() => {
                    setSelectedMonth(m.value);
                    setIsMonthPickerOpen(false);
                  }}
                  style={{
                    padding: "16px",
                    border:
                      m.value === selectedMonthOption?.value
                        ? "2px solid #2563EB"
                        : "1px solid #E5E7EB",
                    borderRadius: "8px",
                    background:
                      m.value === selectedMonthOption?.value
                        ? "#EFF6FF"
                        : "white",
                    cursor: "pointer",
                    textAlign: "center",
                    fontWeight:
                      m.value === selectedMonthOption?.value ? 600 : 400,
                    color: "#1F2937",
                  }}
                >
                  {m.display}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="quote-panel-header">
        <div style={{ width: 36 }} />
        <div
          id="quote-panel-header-title"
          className="header-title"
          style={{ fontWeight: 700, fontSize: "18px" }}
        >
          Quote
        </div>
        <button
          type="button"
          id="quote-filter-icon-btn"
          className="filter-icon-btn"
          onClick={() => {
            if (!isSearchOpen) {
              prevExpandWithoutRef.current = expandWithout;
              prevExpandSurroundingRef.current = expandSurrounding;
            }
            setIsSearchOpen((s) => !s);
            if (isSearchOpen) {
              setSearchText("");
              if (prevExpandWithoutRef.current !== null)
                setExpandWithout(prevExpandWithoutRef.current);
              if (prevExpandSurroundingRef.current !== null)
                setExpandSurrounding(prevExpandSurroundingRef.current);
            }
          }}
        >
          <img src={icons.FilterIcon} alt="Filter Icon" />
        </button>
      </div>

      <div className="quote-panel-content" id="quote-panel-content-id">
        <div id="search-result-title" className="section-title">
          {companyName} Locations
        </div>

        {isSearchOpen && (
          <div
            id="quote-panel-filter-container"
            style={{ padding: "8px 16px" }}
          >
            <input
              id="quote-panel-filter-input"
              ref={searchInputRef}
              aria-label="Filter locations"
              className="quote-filter-input"
              placeholder="Filter locations"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 8,
                border: "1px solid #E5E7EB",
              }}
            />
            {searchText && (
              <button
                id="quote-panel-filter-clear-btn"
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setSearchText("");
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  setSearchText("");
                }}
              >
                <img
                  id="mobile-circle-close-icon"
                  src={icons.CircleCloseIcon}
                  alt="Clear"
                />
              </button>
            )}
          </div>
        )}

        {loading && (
          <div style={{ padding: "60px 0", textAlign: "center" }}>
            <LoadingIcon height={60} width={60} color="#60A5FA" />
          </div>
        )}

        {!loading &&
          kochOrigins.length === 0 &&
          filtered.availableFiltered.length === 0 &&
          competitorOrigins.length === 0 && (
            <div className="empty-state-container-no-origin">
              <div className="empty-state-content">
                <div className="empty-state-text">
                  No Origins Added For This Product
                </div>
                <div className="empty-state-subtitle">
                  Tap the Origins button to the left of the search bar below
                </div>
              </div>
            </div>
          )}

        {!loading && filtered.availableFiltered.length > 0 && (
          <div id="quote-primary-list">
            {filtered.availableFiltered.map((item) => (
              <LocationQuoteCard
                key={`${item.destination.name}-${item.destination.city || ""}`}
                item={item}
                onSelect={setSelectedItem}
                onLocationNameClick={(selected) => {
                  setLocationValidationTrigger((prev) => prev + 1);
                  setSelectedItem(selected);
                }}
              />
            ))}
          </div>
        )}

        {filtered.withoutFiltered.length > 0 && (
          <div id="location-without-product" className="collapsible-section">
            <div
              role="button"
              tabIndex={0}
              className="collapsible-header"
              onClick={() => setExpandWithout(!expandWithout)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setExpandWithout(!expandWithout);
              }}
            >
              <div className="collapsible-title-row">
                <img
                  src={icons.LocationWithoutIcon}
                  alt="Location Without Icon"
                  className="collapsible-icon"
                />
                <div className="collapsible-content-wrapper">
                  <div className="collapsible-text">
                    Locations Without{" "}
                    {formatProductName(effectiveSelectedProduct)}
                  </div>
                  <div className="collapsible-count">
                    {filtered.withoutFiltered.length} Locations
                  </div>
                </div>
              </div>
              {expandWithout ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>
            {expandWithout && (
              <div className="collapsible-body">
                {filtered.withoutFiltered.map((wp) => (
                  <div
                    key={`${wp.destination.name}-${wp.destination.city || ""}`}
                    style={{
                      padding: "12px 16px",
                      background: "white",
                      borderBottom: "1px solid #eee",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ color: "#666" }}>{wp.destination.name}</div>
                    <Button
                      sx={{
                        borderRadius: "20px",
                        border: "1px solid #E2E8EB",
                        padding: "6px 12px",
                        justifyContent: "center",
                        boxShadow: "0 1px 2px 0 rgba(107, 114, 128, 0.20)",
                        alignItems: "center",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#2563EB",
                        minWidth: "auto",
                        whiteSpace: "nowrap",
                      }}
                      onClick={async () => {
                        if (
                          wp.destination.details?.occupants.length === 1 &&
                          wp.destination.id
                        ) {
                          await addProductToOccupant(
                            wp.destination.details.occupants[0],
                            wp.destination.id,
                            wp.destination.name,
                          );
                        } else {
                          setDestinationToTroubleshoot(wp.destination as any);
                          setTroubleshootDestinationOpen(true);
                        }
                      }}
                    >
                      + Add Product
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {surroundingLocs && filtered.surroundingFiltered.length > 0 && (
          <div id="quote-location-surrounding" className="collapsible-section">
            <div
              role="button"
              tabIndex={0}
              className="collapsible-header"
              onClick={() => setExpandSurrounding(!expandSurrounding)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                  setExpandSurrounding(!expandSurrounding);
              }}
            >
              <div className="collapsible-title-row">
                <img
                  src={icons.LocationIcon}
                  alt="Location Icon"
                  className="collapsible-icon"
                />
                <div className="collapsible-content-wrapper">
                  <div className="collapsible-text">
                    Locations In Surrounding Area
                  </div>
                  <div className="collapsible-count">
                    {filtered.surroundingFiltered.length} Locations
                  </div>
                </div>
              </div>
              {expandSurrounding ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </div>

            {expandSurrounding && (
              <div className="collapsible-body">
                {filtered.surroundingFiltered.map((item: any) => {
                  const summary = determineDisplayValues(item.quotes);
                  return (
                    <LocationQuoteCard
                      key={`${item.destination.name}-${
                        item.destination.city || ""
                      }`}
                      item={{ ...item, summary }}
                      onSelect={setSelectedItem}
                      onLocationNameClick={(selected) => {
                        setLocationValidationTrigger((prev) => prev + 1);
                        setSelectedItem(selected);
                      }}
                    />
                  );
                })}
              </div>
            )}
          </div>
        )}

        {searchText &&
          filtered.availableFiltered.length === 0 &&
          filtered.withoutFiltered.length === 0 &&
          filtered.surroundingFiltered.length === 0 && (
            <div className="empty-state-container-no-origin">
              <div className="empty-state-content">
                <div className="empty-state-text">No locations found</div>
              </div>
            </div>
          )}
      </div>

      <div className="floating-footer">
        <div className="quote-sub-footer-row">
          {renderMonthButton()}
          {renderProductButton()}
        </div>

        <div id="search-bar-quote-panel" className="footer-main-row">
          <button
            id="origins-btn-quote-panel"
            type="button"
            className="origins-btn-circle"
            onClick={onOpenOrigins}
          >
            <img
              id="build-icon"
              src={icons.BuildIconWhiteBackground}
              alt="Build Icon"
            />
            <div className="origin-text">Origins</div>
          </button>

          <div
            id="quote-search-pill"
            role="button"
            tabIndex={0}
            className="search-pill"
            onClick={() => {
              onBack(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onBack(undefined);
            }}
          >
            <SearchIcon className="search-icon-sm" />
            <span id="search-pill-text" className="search-pill-text">
              {companyName}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiLocQuotePanel;
